// src/services/menu.service.js
import { http } from "../lib/http";
import { getUser } from "../lib/authToken";
import { DEV_USER } from "../config/localAuth";
import menuData from "../data/menu.json";

// GET
export async function fetchMenuItems({ orgId, location }) {
  const user = getUser() || DEV_USER;
  orgId = orgId || user.restaurant_id;
  location = location || user.location;
  const qs = new URLSearchParams({ orgId: String(orgId), location });
  try {
    return await http.get(`/menu/menu-items/?${qs.toString()}`);
  } catch (error) {
    console.warn("API call failed, falling back to mock data:", error);
    return menuData.items;
  }
}

// PUT (single-item)
export function updateMenuItem({
  item_id,
  price,
  available,
  orgId,
  location,
  is_gst,
  item_name,
  category,
  kot_ip,
  image_url,
}) {
  const user = getUser() || DEV_USER;
  orgId = orgId || user.restaurant_id;
  location = location || user.location;
  const qs = new URLSearchParams({ orgId: String(orgId), location });
  const patch = {
    item_id,
    ...(item_name !== undefined ? { item_name } : {}),
    ...(category !== undefined ? { category } : {}),
    ...(price !== undefined ? { price } : {}),
    ...(available !== undefined ? { available } : {}),
    ...(is_gst !== undefined ? { is_gst } : {}),
    ...(kot_ip !== undefined ? { kot_ip } : {}),
    ...(image_url !== undefined ? { image_url } : {}),
  };
  return retryRequest(() => http.put(`/menu/update?${qs.toString()}`, [patch]));
}

// POST (add single item)
export function addMenuItem(
  { item_name, price, available, category, subcategory, is_gst, kot_ip, image_url },
  { orgId, location } = {}
) {
  const user = getUser() || DEV_USER;
  orgId = orgId || user.restaurant_id;
  location = location || user.location;
  const qs = new URLSearchParams({ orgId: String(orgId), location });
  const body = {
    item_name,
    price: Number(price),
    available: !!available,
    category,
    subcategory,
    is_gst: !!is_gst,
    ...(kot_ip !== undefined ? { kot_ip } : {}),
    ...(image_url !== undefined ? { image_url } : {}),
  };
  return retryRequest(() => http.post(`/menu/add-items?${qs.toString()}`, body));
}

// Helper function for retrying requests
async function retryRequest(requestFn, maxRetries = 3, delay = 1000) {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      if (attempt === maxRetries || !error.message.includes('500:')) {
        throw error;
      }
      const currentDelay = delay; // Capture current delay value for the callback
      console.warn(`Request failed (attempt ${attempt}/${maxRetries}), retrying in ${currentDelay}ms:`, error.message);
      await new Promise(resolve => setTimeout(resolve, currentDelay));
      delay *= 2; // Exponential backoff
    }
  }
}

// PUT (bulk)
export function bulkUpdateMenu(changes, { orgId, location } = {}) {
  if (!Array.isArray(changes) || !changes.length) {
    return Promise.resolve({ status: "noop", updated: 0 });
  }
  const user = getUser() || DEV_USER;
  orgId = orgId || user.restaurant_id;
  location = location || user.location;
  const qs = new URLSearchParams({ orgId: String(orgId), location });
  const body = changes.map(
    ({ item_id, price, available, is_gst, item_name, category, kot_ip, image_url }) => ({
      item_id,
      ...(item_name !== undefined ? { item_name } : {}),
      ...(category !== undefined ? { category } : {}),
      ...(price !== undefined ? { price } : {}),
      ...(available !== undefined ? { available } : {}),
      ...(is_gst !== undefined ? { is_gst } : {}),
      ...(kot_ip !== undefined ? { kot_ip } : {}),
      ...(image_url !== undefined ? { image_url } : {}),
    })
  );
  return retryRequest(() => http.put(`/menu/update?${qs.toString()}`, body));
}

export function fetchMenuItemDetails({ item_id, orgId }) {
  const qs = new URLSearchParams({
    item_id: String(item_id),
    orgId: String(orgId),
  });

  return http.get(`/menu/item-details?${qs.toString()}`);
}

// KOT (Kitchen Order Ticket) APIs
export async function fetchKOTLocations({ orgId } = {}) {
  const user = getUser() || DEV_USER;
  orgId = orgId || user.restaurant_id;
  const qs = new URLSearchParams({ orgId: String(orgId) });
  try {
    return await http.get(`menu/kot-ips?${qs.toString()}`);
  } catch (error) {
    console.warn("KOT API call failed, falling back to mock data:", error);
  }
}
